import { daysFromToday, humanDate } from "@/lib/time/human-date";

/**
 * The words the inbox is read in.
 *
 * A DELIVERY IS NOT A NOTIFICATION. The in-app row is written the moment the event fires; the
 * e-mail or SMS behind it is an attempt that can still be failing an hour later. Showing only
 * the row answers "was I told", and the question people actually ask when something went wrong
 * is "did it go out" — which is what these statuses are for.
 */
export const DELIVERY_STATUS_LABELS: Record<string, string> = {
  PENDING: "Kuyrukta",
  SENT: "Gönderildi",
  DELIVERED: "Ulaştı",
  FAILED: "Gönderilemedi",
  SKIPPED: "Atlandı",
};

export const DELIVERY_STATUS_TONE: Record<
  string,
  "neutral" | "info" | "success" | "warning" | "danger"
> = {
  PENDING: "neutral",
  SENT: "info",
  DELIVERED: "success",
  FAILED: "danger",
  /** Not a failure: a quiet-hours rule or a preference switched off. */
  SKIPPED: "neutral",
};

export const CHANNEL_LABELS: Record<string, string> = {
  IN_APP: "Uygulama içi",
  EMAIL: "E-posta",
  SMS: "SMS",
  PUSH: "Anlık bildirim",
};

/**
 * The event families a notification belongs to — keyed by the SERVER's template keys, which are
 * the event types (`altek-erp-backend/src/events/event-catalog.ts`: `leave_request.approved`,
 * `chat_message.sent`, …) plus the retired `hr.birthday.upcoming` digest still sitting in inboxes.
 *
 * NEVER THE RAW KEY (İK müdürü turu, 7 Ekim 2026). This map used to be keyed by upper-case names
 * the server never sends (`LEAVE_REQUEST_SUBMITTED`), so nearly every chip on «Bildirimlerim» read
 * `hr.birthday.upcoming` or `chat_message.sent`. A key nobody has named yet now reads «Bildirim»:
 * the reader is a person, and a dotted identifier is not a word to them. The old upper-case keys
 * stay below — harmless, and some rows written before the rename may carry them.
 */
export const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  // hesap ve güvenlik
  "user.invited": "Davet",
  "user.disabled": "Hesap kapatıldı",
  "user.password_changed": "Parola değişti",
  "session.suspicious": "Güvenlik uyarısı",
  "device.first_seen": "Yeni cihaz",
  // İK
  "employee.hired": "İşe giriş",
  "employment.terminated": "İşten çıkış",
  "employment.probation_ending": "Deneme süresi",
  "leave_request.submitted": "İzin talebi",
  "leave_request.approved": "İzin onaylandı",
  "leave_request.rejected": "İzin reddedildi",
  "leave_request.cancelled": "İzin iptal edildi",
  "employee_document.expiring": "Belge süresi doluyor",
  "employee_document.submitted": "Belge yüklendi",
  "employee_document.reviewed": "Belge incelendi",
  // ik-belge 20 Ekim 2026: İK belgede değişiklik yaptı / belgeyi kaldırdı.
  "employee_document.changed": "Belgede değişiklik",
  "employee_document.removed": "Belge kaldırıldı",
  "hr.birthday.upcoming": "Doğum günü",
  // onay akışı ve talepler
  "approval.requested": "Onay bekliyor",
  "approval.decided": "Onay sonucu",
  "approval.escalated": "Onay devredildi",
  // F2a 1 Kasım 2026: akışın bildirim adımı — bilgi içindir, karar istemez, okununca kapanır.
  "approval.notify": "Bilgilendirme",
  "presentation_request.decided": "Sunum talebi",
  "product_request.decided": "Ürün talebi",
  // masraf beyanı ve ödeme — finans F2b/F2c (2 Kasım 2026); hepsi yükte `link` taşıyor
  "expense_claim.decided": "Masraf beyanı sonucu",
  "expense_claim.paid": "Masraf ödemesi",
  "expense_claim.chain_empty": "Masraf onay zinciri boş",
  "finance_payable.due": "Ödeme vadesi",
  // F5 eki, 3 Kasım 2026: bütçe sürümü patronun onayında; yükte `link` (`/finance/budgets?tab=lines&year=…&company=…`).
  "finance_budget.submitted": "Bütçe onayı",
  // stok ve operasyon
  // satış → depo: onaylı sipariş sevk bekliyor (satis-duzeltme 20 Ekim 2026, S-1)
  "sales_order.confirmed": "Sevk bekleyen sipariş",
  "inventory_document.posted": "Stok belgesi",
  "inventory_document.cancelled": "Stok belgesi iptal edildi",
  "stock.below_reorder_point": "Kritik stok",
  "lot.expiring": "Parti son kullanma",
  "stock_count.posted": "Sayım",
  "transport_job.dispatched": "Sevkiyat",
  "transport_job.completed": "Sevkiyat tamamlandı",
  "transport_job.cancelled": "Sevkiyat iptal edildi",
  "operation_exception.raised": "Operasyon sapması",
  "gate_visit.opened": "Saha girişi",
  "vehicle_document.expiring": "Araç belgesi",
  "driver_licence.expiring": "Sürücü belgesi",
  // şirket içi filo — 10 Ekim 2026 (`fleet.reminder-scan` ve sürücünün hasar bildirimi)
  "fleet_document.expiring": "Araç belgesi",
  "fleet_maintenance.due": "Araç bakımı",
  "fleet_assignment.return_overdue": "Araç iadesi",
  "fleet_assignment.return_overdue_holder": "Araç iadesi",
  "fleet_installment.due": "Araç taksiti",
  "fleet_damage.reported": "Araç hasarı",
  // zimmet talebi — 30 Ekim 2026 (açılış İK'ya, sonuç talep edene; ikisi de yükte `link` taşıyor)
  "asset_request.submitted": "Zimmet talebi",
  "asset_request.decided": "Zimmet talebi sonucu",
  // görev ve etkinlik — pz-gorev 6 Ekim 2026 (M7, M8)
  "task.assigned": "Görev",
  "task_comment.created": "Görev mesajı",
  "event.stand_visits_shared": "Stant ziyaretleri",
  // çalışma alanı ve platform
  "chat_message.sent": "Mesaj",
  // sohbet-duzeltme 20 Ekim 2026 (H8): grupta @ad ile anılan kişiye, sessize almış olsa da.
  "chat_message.mentioned": "Bahsetme",
  "converter_job.finished": "Dosya dönüştürme",
  "channel_import.finished": "Kanal raporu",
  "file.infected": "Zararlı dosya",
  "integrity_finding.raised": "Bütünlük bulgusu",
  "posting_period.closed": "Dönem kapandı",
  "export.completed": "Dışa aktarım hazır",
  // eski büyük harfli anahtarlar
  LEAVE_REQUEST_SUBMITTED: "İzin talebi",
  LEAVE_REQUEST_DECIDED: "İzin kararı",
  APPROVAL_ASSIGNED: "Onay bekliyor",
  DOCUMENT_EXPIRING: "Belge süresi doluyor",
  MEDICAL_REPORT_SUBMITTED: "Sağlık raporu",
  STOCK_REORDER: "Kritik stok",
  ITS_NOTIFICATION_FAILED: "İTS bildirimi başarısız",
  SECURITY_ALERT: "Güvenlik uyarısı",
  ANNOUNCEMENT: "Duyuru",
};

/** Adı konmamış bir tür — ham anahtar ASLA ekrana çıkmıyor. */
export const GENERIC_NOTIFICATION_LABEL = "Bildirim";

export function notificationTypeLabel(type: string): string {
  return NOTIFICATION_TYPE_LABELS[type] ?? GENERIC_NOTIFICATION_LABEL;
}

/**
 * Bildirimin götürdüğü ekran — olay yükünün `link` değişkeni (pz-gorev 6 Ekim 2026).
 *
 * Görev ve stant ziyareti bildirimleri kaydın kendisine götürüyor (`/tasks/<id>`,
 * `/events/<id>/stand-visits`). YALNIZ UYGULAMA İÇİ GÖRELİ YOL kabul ediliyor: `/` ile başlayan,
 * `//` ile başlamayan, ters bölü taşımayan. Değişkenler olay yükünden geliyor; bir gün dış bir
 * adres taşırlarsa ekran onu izlememeli.
 */
export function notificationLink(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const variables = (data as { variables?: unknown }).variables;
  if (!variables || typeof variables !== "object") return null;
  const link = (variables as { link?: unknown }).link;
  if (typeof link !== "string") return null;
  if (!link.startsWith("/") || link.startsWith("//") || link.includes("\\")) return null;
  return link;
}

/** The subject a notification is about, as the dispatcher wrote it (`data.entityRef`). */
function entityRefOf(data: unknown): { type: string; id: string } | null {
  if (!data || typeof data !== "object") return null;
  const ref = (data as { entityRef?: unknown }).entityRef;
  if (!ref || typeof ref !== "object") return null;
  const { type, id } = ref as { type?: unknown; id?: unknown };
  return typeof type === "string" && typeof id === "string" && id !== "" ? { type, id } : null;
}

function variableOf(data: unknown, name: string): string | null {
  if (!data || typeof data !== "object") return null;
  const variables = (data as { variables?: unknown }).variables;
  if (!variables || typeof variables !== "object") return null;
  const value = (variables as Record<string, unknown>)[name];
  return typeof value === "string" && value !== "" ? value : null;
}

/**
 * WHERE A NOTIFICATION TAKES THE READER (İK müdürü turu, 7 Ekim 2026).
 *
 * The bell sent every row to `/notifications` — the list the reader was already reading about.
 * In order:
 *   1. the event's own `link` variable (tasks, stand visits — `notificationLink`);
 *   2. the subject the dispatcher recorded (`data.entityRef`) for the types whose screen is known
 *      and whose RECIPIENT can open it: a chat message → its conversation; a leave request sent,
 *      decided or cancelled → the requester's own list with the request open; a document review →
 *      «Belgelerim»; a document İK must decide → Personel › Belgeler; a probation ending → the
 *      person's card;
 *   3. nothing (`null`): the caller falls back to the inbox. A guessed screen the recipient may not
 *      open would trade a useless link for a refusal.
 *
 * THE RETIRED BIRTHDAY DIGEST GOES NOWHERE (second review, 7 Ekim 2026). `hr.birthday.upcoming`
 * carries a COMPANY as its subject and no person id; it was first sent to «Duyurular», but the
 * birthday announcement there is shown only to the İK müdürü role and the digest's old recipients
 * read «Duyuru yok». No target → no «Kayda git», and the bell keeps the row in the inbox.
 *
 * Only relative in-app paths are returned; ids are URL-encoded.
 */
export function notificationTarget(row: { type: string; data: unknown }): string | null {
  const explicit = notificationLink(row.data);
  if (explicit) return explicit;

  const ref = entityRefOf(row.data);
  const id = ref ? encodeURIComponent(ref.id) : null;

  switch (row.type) {
    case "chat_message.sent":
    case "chat_message.mentioned":
      return ref?.type === "chat.conversation" && id ? `/chat/${id}` : "/chat";
    /**
     * The submission and the cancellation go to the same place as the decision (kullanılabilirlik
     * turu, 8 Ekim 2026): «İzin talebi onaya gönderildi» had no «Kayda git», so the requester
     * reading it could not reach the request it was about. All four carry the request as their
     * subject and all four are addressed to its owner first (`event-consumers.ts`).
     */
    case "leave_request.submitted":
    case "leave_request.approved":
    case "leave_request.rejected":
    case "leave_request.cancelled":
      return id ? `/my-leave/requests?record=${id}` : "/my-leave/requests";
    case "employee_document.reviewed":
    case "employee_document.changed":
    case "employee_document.removed":
      return "/company/my-documents";
    case "employee_document.submitted":
      return "/hr/people/documents";
    case "employee_document.expiring":
      return "/hr/people/documents?view=expiring";
    case "employment.probation_ending": {
      const employeeId = variableOf(row.data, "employeeId");
      return employeeId ? `/hr/employees/${encodeURIComponent(employeeId)}` : null;
    }
    case "approval.requested":
    case "approval.escalated":
      return "/approvals";
    /**
     * F2a, 1 Kasım 2026: bildirim adımının alıcısının Onay Merkezi'nde işi YOK — bilgi verildi, karar
     * istenmedi. Gideceği yer kaydın kendi sayfası; onu akışı başlatan modül anlık görüntüye `link` olarak
     * yazıyorsa sunucu yüke koyuyor ve yukarıdaki `notificationLink` kazanıyor. Yazmıyorsa bağlantı yok.
     */
    case "approval.notify":
      return null;
    /**
     * MASRAF BEYANI (finans F2c-1, 2 Kasım 2026): sonuç ve ödeme beyan sahibine gidiyor; sunucu yüke `link`
     * (`/my-expenses/<id>`) yazıyor ve yukarıda o kazanıyor. Bu satır adres taşımayan eski bir satır için.
     */
    case "expense_claim.decided":
    case "expense_claim.paid":
      return ref?.type === "finance.expense_claim" && id ? `/my-expenses/${id}` : "/my-expenses";
    /**
     * A conversion is announced only to the person who asked for it (`event-consumers.ts`), who
     * holds the key to start one — so the converter's own screen, where the result is
     * (9 Ekim 2026, 5. tur: the row went back to the inbox it was read in).
     */
    case "converter_job.finished":
      return "/converter";
    /**
     * ŞİRKET İÇİ FİLO (10 Ekim 2026). Sunucu her yükte `link` yazıyor (araç kartı, ilgili sekmesiyle)
     * ve yukarıda o kazanıyor; bu satırlar yalnız adres taşımayan bir satır için: araç kartı. Aracı
     * elinde tutan kişiye giden geciken iade kartı AÇAMAYAN birine gidiyor (`fleet.vehicle.read`
     * yok) — onun yeri Panelim › «Aracım».
     */
    case "fleet_document.expiring":
    case "fleet_maintenance.due":
    case "fleet_assignment.return_overdue":
    case "fleet_installment.due":
    case "fleet_damage.reported": {
      const fleetVehicleId = variableOf(row.data, "fleetVehicleId");
      return fleetVehicleId ? `/fleet/vehicles/${encodeURIComponent(fleetVehicleId)}` : null;
    }
    case "fleet_assignment.return_overdue_holder":
      return "/my-panel";
    case "hr.birthday.upcoming":
      return null;
    default:
      return ref?.type === "chat.conversation" && id ? `/chat/${id}` : null;
  }
}

/**
 * «NE YAPILACAK» — kayda giden düğmenin adı (3 Kasım 2026, tasarım turu 2). Her satırda aynı «Kayda git» yazıyordu; kişi bildirimin kendisinden iş mi istediğini,
 * yalnız haber mi verdiğini okuyamıyordu. Ad türün ailesinden; adı konmamış türde «Kayda git». Gidecek yer `notificationTarget`'ın işi, burada yalnız söz var.
 */
const ACTION_LABELS: ReadonlyArray<readonly [string, string]> = [
  ["approval.requested", "Karar ver"],
  ["approval.escalated", "Karar ver"],
  ["leave_request", "Talebi aç"],
  ["presentation_request", "Talebi aç"],
  ["product_request", "Talebi aç"],
  ["asset_request", "Talebi aç"],
  ["employee_document", "Belgeyi aç"],
  ["inventory_document", "Belgeyi aç"],
  ["chat_message", "Mesaja git"],
  ["task", "Görevi aç"],
  ["event.", "Etkinliği aç"],
  ["expense_claim", "Beyanı aç"],
  ["finance_payable", "Ödemeyi aç"],
  ["sales_order", "Siparişi aç"],
  ["transport_job", "Seferi aç"],
  ["fleet_", "Aracı aç"],
  ["employment.", "Kişiyi aç"],
  ["converter_job", "Sonucu aç"],
  ["export", "Sonucu aç"],
];

export function notificationActionLabel(type: string): string {
  return ACTION_LABELS.find(([prefix]) => type.startsWith(prefix))?.[1] ?? "Kayda git";
}

/** Gelen kutusunun grubu: okunmamışlar kendi başlığında, ötekiler günlerine göre. */
export interface InboxGroup {
  key: string;
  label: string;
}

/** Sunucunun anı → İstanbul günü → başlık. Yalnız yakın iki gün söze dönüşüyor; ötesi tarih. */
export function inboxDayLabel(day: string, today: string): string {
  const days = daysFromToday(day, today);
  if (days === 0) return "Bugün";
  if (days === -1) return "Dün";
  return humanDate(day);
}

/**
 * OKUNMAMIŞ ÖNDE, GERİSİ GÜN GÜN. Sunucu `createdAt`'e göre sıralıyor ve okunmamışı öne alan bir sıralaması yok (`readAt` ile sıralamak okunmuşları okunduğu ana göre dizerdi):
 * öne alma bu SAYFANIN içinde, sıra bozulmadan. Sayfa 50 satır ve okunmamış çoğunlukla ilk sayfada; daha eskisi kendi sayfasının başında görünür, «Okunmamış» segmenti hepsini verir.
 */
export function inboxOrder<T extends { isRead: boolean }>(rows: readonly T[], unreadFirst: boolean): T[] {
  if (!unreadFirst) return [...rows];
  return [...rows.filter((row) => !row.isRead), ...rows.filter((row) => row.isRead)];
}

/** `2 dakika önce`, `3 saat önce`, `12 Eyl 2026`. Beyond a week, a date reads better. */
export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const minutes = Math.round((Date.now() - then) / 60_000);
  if (minutes < 1) return "az önce";
  if (minutes < 60) return `${minutes} dakika önce`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} saat önce`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} gün önce`;
  // `sv-SE`: okuyanın günü, `YYYY-AA-GG`; ISO dizgesinin ilk on hanesi UTC günüdür.
  return humanDate(new Date(iso).toLocaleDateString("sv-SE"));
}

/**
 * TELEFONDA DOKUNMA HEDEFİ 44 PİKSEL — Panelim kutularının başlık bağlantısı («Tümünü gör»)
 * (kullanılabilirlik turu 2, 9 Ekim 2026). 375 pikselde 67×16 piksel bir metindi;
 * `responsive-and-mobile.md` HARD RULE: 768'in altında her hedef en az 44×44. Panelim'in kendi
 * bloklarındaki desenin aynısı (`PanelBlock` › `TOUCH_LINK`); masaüstünde metin bağlantısı.
 */
export const CARD_TOUCH_LINK = "max-md:inline-flex max-md:min-h-11 max-md:items-center";

/**
 * Panelim kutusunun SATIRI tek bağlantı ve satırın tamamı (iç boşluk dahil) — yalnız metin
 * bağlantıyken telefonda 19 piksellik bir şeritti. Odak bir satır gibi söylenir (`row-focus`).
 */
export const CARD_ROW_LINK =
  "group/row row-focus block min-h-11 px-4 py-3 transition-colors duration-fast can-hover:bg-surface-2";
